import { dataService } from './api.js';
import { dataMeet } from '../data/data_meet.js';

// API endpoints for meeting coordinator operations
const MEETING_COORDINATOR_ENDPOINTS = {
  // Guest token endpoint (for future implementation)
  GET_GUEST_TOKEN: 'api/meeting/guest-token',
  
  // Start ad-hoc meeting
  START_ADHOC_MEETING: 'api/meeting/adhoc/start',
  
  // Validate meeting ID
  VALIDATE_MEETING_ID: 'api/meeting/validate',
  
  // Validate instance ID
  VALIDATE_INSTANCE_ID: 'api/meeting/instance/validate'
};

/**
 * Get guest token from one-time code
 * @param {string} guestCode - The one-time guest code
 * @returns {Promise<Object>} - Token and instance ID
 */
export const getGuestToken = async (guestCode) => {
  try {
    const response = await dataService.requestData(
      `${MEETING_COORDINATOR_ENDPOINTS.GET_GUEST_TOKEN}?code=${encodeURIComponent(guestCode)}`,
      'GET'
    );
    
    if (response.success) {
      // Store token in cookie
      document.cookie = `guest_token=${response.token}; path=/; max-age=3600`; // 1 hour expiry
      return {
        success: true,
        token: response.token,
        instanceId: response.instanceId
      };
    } else {
      throw new Error(response.message || 'Failed to get guest token');
    }
  } catch (error) {
    console.error('Error getting guest token:', error);
    throw error;
  }
};

/**
 * Load completed meeting details
 * @param {string} instanceId - The instance ID of the completed meeting
 * @returns {Promise<Object>} - Meeting data with all details
 */
export const loadCompletedMeeting = async (instanceId) => {
  try {
    console.log('🔍 Loading completed meeting data for instance:', instanceId);
    
    // Use the existing getCompletedMeeting method from data_meet.js
    const response = await dataMeet.getCompletedMeeting(instanceId);
    
    console.log('📋 Raw response from getCompletedMeeting:', response);
    
    if (response.success) {
      // The response is already formatted by getCompletedMeeting
      const formattedData = {
        success: true,
        instance: response.instance || { id: instanceId },
        objectives: response.objectives || [],
        questions: response.questions || [],
        suggestions: response.suggestions || [],
        transcriptions: response.transcription ? {
          raw: response.transcription.rawTranscription || '',
          formatted: response.transcription.formattedTranscription || response.transcription.rawTranscription || ''
        } : { raw: '', formatted: '' },
        actionItems: response.actionItems || [],
        wrapUp: response.wrapUp || [],
        participants: response.participants || [],
        timeline: response.timeline || null
      };
      
      console.log('✅ Formatted completed meeting data:', formattedData);
      return formattedData;
    } else {
      throw new Error(response.message || 'Failed to load completed meeting');
    }
  } catch (error) {
    console.error('❌ Error loading completed meeting:', error);
    throw error;
  }
};

/**
 * Start an ad-hoc meeting
 * @param {Object} meetingConfig - Meeting configuration
 * @param {string} meetingConfig.meetingName - Name of the meeting
 * @param {string} meetingConfig.objectives - Overall objectives
 * @param {string} meetingConfig.specificTasks - Specific tasks (one per line)
 * @param {string} meetingConfig.coachingStrategy - Coaching strategy pointers (one per line)
 * @returns {Promise<Object>} - Meeting instance data
 */
export const startAdhocMeeting = async (meetingConfig) => {
  try {
    // Parse the text inputs into structured data
    const specificTasks = meetingConfig.specificTasks
      .split('\n')
      .map(task => task.trim())
      .filter(task => task.length > 0);
    
    const coachingStrategy = meetingConfig.coachingStrategy
      .split('\n')
      .map(strategy => strategy.trim())
      .filter(strategy => strategy.length > 0);
    
    const payload = {
      meetingName: meetingConfig.meetingName,
      objectives: {
        overall: meetingConfig.objectives,
        specificTasks: specificTasks
      },
      coachingStrategy: coachingStrategy
    };
    
    const response = await dataService.requestData(
      MEETING_COORDINATOR_ENDPOINTS.START_ADHOC_MEETING,
      'POST',
      payload
    );
    
    if (response.success) {
      return {
        success: true,
        instanceId: response.instanceId,
        meetingData: response.meetingData
      };
    } else {
      throw new Error(response.message || 'Failed to start ad-hoc meeting');
    }
  } catch (error) {
    console.error('Error starting ad-hoc meeting:', error);
    throw error;
  }
};

/**
 * Validate a Liminal meeting ID
 * @param {string} meetingId - The meeting ID to validate
 * @returns {Promise<Object>} - Validation result with meeting details if valid
 */
export const validateMeetingId = async (meetingId) => {
  try {
    const response = await dataService.requestData(
      `${MEETING_COORDINATOR_ENDPOINTS.VALIDATE_MEETING_ID}?meetingId=${encodeURIComponent(meetingId)}`,
      'GET'
    );
    
    if (response.success) {
      return {
        success: true,
        isValid: true,
        meetingData: response.meetingData
      };
    } else {
      return {
        success: true,
        isValid: false,
        message: response.message || 'Invalid meeting ID'
      };
    }
  } catch (error) {
    console.error('Error validating meeting ID:', error);
    return {
      success: false,
      isValid: false,
      message: 'Failed to validate meeting ID'
    };
  }
};

/**
 * Validate an instance ID and get meeting details
 * @param {string} instanceId - The instance ID to validate
 * @returns {Promise<Object>} - Validation result with meeting details
 */
export const validateInstanceId = async (instanceId) => {
  try {
    // console.log('🔍 Validating instance ID:', instanceId);
    
    const response = await dataMeet.getInstance(instanceId);
    
    if (response && response.success && response.instance) {
      // console.log('✅ Instance found, processing data...');
      
      // Transform the instance data to match the expected format
      const meetingData = {
        _id: response.instance.instance_id || response.instance._id,
        instanceId: response.instance.instance_id || response.instance._id,
        entity: { 
          name: response.instance.name,
          id: response.instance.instance_id || response.instance._id
        },
        name: response.instance.name,
        short_id: response.instance.short_id,
        description: response.instance.description,
        meetingId: response.instance.instance_id || response.instance._id,
        state: response.instance.state,
        instance_type: response.instance.instance_type,
        training_id: response.instance.training_id,  // ← ADD THIS LINE
        created_at: response.instance.created_at,
        updated_at: response.instance.updated_at
      };
      
      // console.log('✅ Meeting data prepared for viewer:', meetingData.name);
      
      return {
        success: true,
        isValid: true,
        meetingData: meetingData,
        isCompleted: response.instance.state === 'completed'
      };
    } else {
      console.warn('Instance not found:', response?.message || 'Unknown error');
      
      return {
        success: true,
        isValid: false,
        message: response?.message || 'Instance not found'
      };
    }
  } catch (error) {
    console.error('Error validating instance ID:', error.message);
    
    return {
      success: false,
      isValid: false,
      message: 'Failed to validate instance ID: ' + error.message
    };
  }
};

export const meetingCoordinatorService = {
  getGuestToken,
  loadCompletedMeeting,
  startAdhocMeeting,
  validateMeetingId,
  validateInstanceId
}; 